import { useToastPortal } from '@chegg-wt/core-wa/hooks/useToastPortal'
import { clearToast } from '@chegg-wt/core-wa/state/actions'
import {
  paperTitleSelector,
  quickActionPanelTypeSelector,
  toastSelector,
} from '@chegg-wt/core-wa/state/selectors'
import React, { useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { Link } from '@chegg-ui/link'
import { Toast } from './styled'
import { ToastMessages } from './constants'

export const TOAST_TIMEOUT = 3000 // 3 seconds

export const ToastPortal = () => {
  const dispatch = useDispatch()
  const routes = useRoutesContext()
  const toastType = useSelector(toastSelector)
  const paperTitle = useSelector(paperTitleSelector)
  const quickActionPanelType = useSelector(quickActionPanelTypeSelector)
  const { loaded, portalId } = useToastPortal(!!quickActionPanelType)
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>()

  const {
    message,
    persist,
    title,
    type,
    iconOverride,
    ctaLinkText,
    ctaLinkOnClickHandler,
  } = useMemo(() => {
    const {
      message,
      persist,
      title,
      type,
      iconOverride,
      ctaLinkText,
      ctaLinkOnClickHandler,
    } = ToastMessages[toastType] ?? {}

    return {
      message: typeof message === 'function' ? message(paperTitle) : message,
      persist,
      title,
      type,
      iconOverride,
      ctaLinkText,
      ctaLinkOnClickHandler,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toastType])

  const handleClose = () => {
    dispatch(clearToast())
    clearTimeout(timeoutIdRef.current)
  }

  useEffect(() => {
    if (!persist && message) {
      timeoutIdRef.current = setTimeout(() => {
        dispatch(clearToast())
      }, TOAST_TIMEOUT)

      return () => {
        handleClose()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, persist])

  return loaded && message
    ? createPortal(
        <Toast
          type={type}
          title={title}
          id="toast-message"
          data-test={`toast-message-${type}`}
          message={message}
          onCloseClick={handleClose}
          iconOverride={iconOverride}
          bannerStyle="inline"
          {...(ctaLinkText && {
            ctaLink: (
              <Link
                id="toast-link"
                onClick={() => ctaLinkOnClickHandler(dispatch, routes)}
              >
                {ctaLinkText}
              </Link>
            ),
          })}
        />,
        document.getElementById(portalId)
      )
    : null
}
